import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
    History, Microscope, FileText, Calendar, 
    User, ChevronRight, Search, Download, 
    AlertCircle, Pill, Activity, Stethoscope 
} from 'lucide-react';

const PatientHealthRecords = ({ initialView = 'medical-history' }) => {
    const [view, setView] = useState(initialView);
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const patientId = localStorage.getItem('patientId') || '1';

    useEffect(() => {
        fetchHistory();
    }, [patientId]);

    const fetchHistory = async () => {
        try {
            const res = await axios.get(`http://localhost:5000/api/clinical/history/${patientId}`);
            setHistory(res.data);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric', month: 'long', day: 'numeric'
        });
    };

    const renderMedicalHistory = () => (
        <div className="space-y-6 animate-in slide-in-from-bottom duration-500">
            {history.map((record) => (
                <div key={record.id} className="bg-white rounded-[40px] p-8 border border-slate-100 shadow-xl shadow-slate-200/20 hover:shadow-2xl transition-all group overflow-hidden relative">
                    <div className="absolute top-0 left-0 w-2 h-full bg-indigo-500" />
                    <div className="flex justify-between items-start mb-6">
                        <div className="flex items-center gap-4">
                            <div className="w-14 h-14 rounded-2xl bg-indigo-50 flex items-center justify-center">
                                <Calendar className="w-6 h-6 text-indigo-600" />
                            </div>
                            <div>
                                <h3 className="text-xl font-black text-slate-800 tracking-tight">{formatDate(record.createdAt)}</h3>
                                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mt-1">Encounter ID: #{record.id}</p>
                            </div>
                        </div>
                        <div className="flex gap-2">
                             <button className="p-3 bg-slate-50 text-slate-400 rounded-2xl hover:bg-slate-100 transition-all"><Download className="w-5 h-5" /></button>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        <div>
                            <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-indigo-400 mb-4 flex items-center gap-2">
                                <Stethoscope className="w-3.5 h-3.5" /> Complaints
                            </h4>
                            <div className="flex flex-wrap gap-2">
                                {(record.complaints || []).map((c, i) => (
                                    <span key={i} className="px-3 py-1.5 bg-slate-50 text-slate-600 rounded-xl text-[10px] font-bold border border-slate-100">{c}</span>
                                ))}
                            </div>
                        </div>
                        <div>
                            <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-emerald-400 mb-4 flex items-center gap-2">
                                <Activity className="w-3.5 h-3.5" /> Diagnosis
                            </h4>
                            <div className="space-y-2">
                                {(record.selectedDiagnosis || []).map((d, i) => (
                                    <p key={i} className="text-xs font-bold text-slate-700 leading-relaxed">
                                        <span className="text-indigo-500 mr-2">{d.code}</span> {d.name}
                                    </p>
                                ))}
                                {!record.selectedDiagnosis?.length && <p className="text-xs font-bold text-slate-300 italic">No formal diagnosis noted</p>}
                            </div>
                        </div>
                        <div>
                            <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-amber-400 mb-4 flex items-center gap-2">
                                <FileText className="w-3.5 h-3.5" /> Plan
                            </h4>
                            <p className="text-xs font-bold text-slate-500 leading-relaxed line-clamp-3">{record.plan || "Follow up as needed."}</p>
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );

    const renderLabResults = () => {
        const labs = history.flatMap(h => (h.investigationRequests || []).map(l => ({ ...l, date: h.createdAt })));
        return (
            <div className="bg-white rounded-[40px] border border-slate-100 shadow-xl overflow-hidden animate-in fade-in duration-500">
                <table className="w-full text-left border-collapse">
                    <thead>
                        <tr className="bg-slate-50/50">
                            <th className="px-8 py-6 text-[10px] font-black uppercase tracking-widest text-slate-400">Date</th>
                            <th className="px-8 py-6 text-[10px] font-black uppercase tracking-widest text-slate-400">Investigation</th>
                            <th className="px-8 py-6 text-[10px] font-black uppercase tracking-widest text-slate-400">Category</th>
                            <th className="px-8 py-6 text-[10px] font-black uppercase tracking-widest text-slate-400">Status</th>
                            <th className="px-8 py-6 text-[10px] font-black uppercase tracking-widest text-slate-400 text-right">Result</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-50">
                        {labs.map((lab, i) => (
                            <tr key={i} className="hover:bg-indigo-50/30 transition-colors group">
                                <td className="px-8 py-6">
                                    <p className="text-[11px] font-black text-slate-800 tracking-tight">{formatDate(lab.date)}</p>
                                </td>
                                <td className="px-8 py-6">
                                    <p className="text-[11px] font-bold text-slate-700">{lab.testName}</p>
                                </td>
                                <td className="px-8 py-6">
                                    <span className={`px-3 py-1 rounded-lg text-[9px] font-black uppercase tracking-widest ${
                                        lab.category === 'rapid' ? 'bg-emerald-50 text-emerald-600' : 'bg-indigo-50 text-indigo-600'
                                    }`}>
                                        {lab.category}
                                    </span>
                                </td>
                                <td className="px-8 py-6">
                                    <div className="flex items-center gap-2">
                                        <div className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
                                        <p className="text-[10px] font-black text-amber-500 uppercase">Pending</p>
                                    </div>
                                </td>
                                <td className="px-8 py-6 text-right">
                                    <button className="px-6 py-2 bg-slate-100 text-slate-400 rounded-xl text-[10px] font-black uppercase tracking-widest group-hover:bg-indigo-600 group-hover:text-white transition-all">View report</button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {labs.length === 0 && (
                    <div className="py-20 text-center opacity-30">
                        <Microscope className="w-12 h-12 mx-auto mb-4 text-slate-400" />
                        <p className="text-sm font-black uppercase tracking-widest">No lab records found</p>
                    </div>
                )}
            </div>
        );
    };

    const renderPrescriptions = () => {
        const meds = history.flatMap(h => (h.prescription || []).map(p => ({ ...p, date: h.createdAt })));
        return (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 animate-in slide-in-from-right duration-500">
                {meds.map((med, i) => (
                    <div key={i} className="bg-white rounded-[40px] p-8 border border-slate-100 shadow-xl space-y-6 hover:translate-y-[-4px] transition-all group relative overflow-hidden">
                        <div className="absolute top-0 right-0 w-24 h-24 bg-emerald-50/50 rounded-bl-[100px] -mr-8 -mt-8 z-0" />
                        <div className="flex justify-between items-start relative z-10">
                            <div className="flex items-center gap-4">
                                <div className="w-12 h-12 rounded-2xl bg-emerald-100 flex items-center justify-center shadow-inner">
                                    <Pill className="w-6 h-6 text-emerald-600" />
                                </div>
                                <div>
                                    <h3 className="text-lg font-black text-slate-800 tracking-tight">{med.name}</h3>
                                    <div className="flex items-center gap-2 mt-1">
                                        <p className="text-[10px] font-black text-indigo-500 uppercase tracking-widest">{med.diagnosisId}</p>
                                        <span className="w-1 h-1 rounded-full bg-slate-300" />
                                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest italic">{formatDate(med.date)}</p>
                                    </div>
                                </div>
                            </div>
                            <span className="px-4 py-2 bg-indigo-50 text-indigo-600 rounded-xl text-[10px] font-black uppercase tracking-widest border border-indigo-100">{med.duration}</span>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4 relative z-10">
                            <div className="bg-slate-50 p-5 rounded-3xl border border-slate-100">
                                <p className="text-[9px] font-black uppercase tracking-[0.2em] text-slate-400 mb-2 ml-1">Instruction</p>
                                <p className="text-[11px] font-bold text-slate-700">{med.when}</p>
                            </div>
                            <div className="bg-slate-50 p-5 rounded-3xl border border-slate-100">
                                <p className="text-[9px] font-black uppercase tracking-[0.2em] text-slate-400 mb-2 ml-1">Daily Schedule</p>
                                <div className="flex gap-2 flex-wrap">
                                    {(med.timing || []).map(t => (
                                        <span key={t} className="px-3 py-1 bg-slate-900 text-white rounded-lg text-[8px] font-black uppercase tracking-widest">{t[0]}</span>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
                {meds.length === 0 && (
                    <div className="col-span-2 py-40 text-center opacity-30">
                        <Pill className="w-16 h-16 mx-auto mb-6 text-slate-400" />
                        <p className="text-sm font-black uppercase tracking-widest">No prescriptions found</p>
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="p-4 md:p-10 bg-[#f8fafc] min-h-screen">
            <header className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 mb-12">
                <div>
                    <h1 className="text-4xl font-black text-slate-900 tracking-tighter uppercase italic">Patient Health Library</h1>
                    <p className="text-xs font-bold text-slate-400 mt-2 uppercase tracking-[0.2em]">Comprehensive medical history & diagnostic reports</p>
                </div>
                <div className="flex gap-2 bg-white p-2 rounded-[32px] shadow-2xl shadow-slate-200/50 border border-slate-100">
                    {[
                        { id: 'medical-history', label: 'History', icon: History },
                        { id: 'lab-results', label: 'Laboratories', icon: Microscope },
                        { id: 'prescriptions', label: 'Medicines', icon: Pill }
                    ].map(t => (
                        <button 
                            key={t.id}
                            onClick={() => setView(t.id)}
                            className={`flex items-center gap-3 px-8 py-3.5 rounded-[24px] text-[10px] font-black uppercase tracking-widest transition-all ${
                                view === t.id ? 'bg-indigo-600 text-white shadow-xl shadow-indigo-100' : 'bg-transparent text-slate-400 hover:text-slate-600'
                            }`}
                        >
                            <t.icon className="w-4 h-4" /> {t.label}
                        </button>
                    ))}
                </div>
            </header>

            {loading ? (
                <div className="h-64 flex flex-col items-center justify-center gap-4 opacity-50">
                    <div className="w-12 h-12 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin" />
                    <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">Retrieving Records...</p>
                </div>
            ) : (
                <div className="max-w-6xl mx-auto">
                    {view === 'medical-history' && renderMedicalHistory()}
                    {view === 'lab-results' && renderLabResults()}
                    {view === 'prescriptions' && renderPrescriptions()}
                </div>
            )}
        </div>
    );
};

export default PatientHealthRecords;
